/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useParkingLotsList.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing the Parking Lots list, integrating with useDataGrid and parkingLotsApi.
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useEffect } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { fetchParkingLots } from '../api/parkingLotsApi';
import type { ParkingLot } from '../types';

interface UseParkingLotsListOptions {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialColumns?: any[];
  initialSortBy?: string;
  initialSortOrder?: 'asc' | 'desc';
  initialLimit?: number;
  domainId?: number;
}

export function useParkingLotsList({
  initialColumns = [],
  initialSortBy = 'name',
  initialSortOrder = 'asc',
  initialLimit = 10,
  domainId
}: UseParkingLotsListOptions = {}) {
  const { t } = useI18n();
  const { showError } = useNotifications();

  const fetchData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        const result = await fetchParkingLots({
          page: params.page,
          limit: params.limit,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          search: params.search,
          domainId
        });

        return {
          data: result.data,
          pagination: result.pagination
        };
      } catch (error) {
        const message = error instanceof Error ? error.message : t('parkingLots.messages.loadError');
        showError(message);
        throw error;
      }
    },
    [showError, t, domainId]
  );

  const availableColumns = useMemo(() => {
    if (initialColumns.length > 0) {
      return initialColumns;
    }

    return [
      {
        key: 'name',
        label: t('parkingLots.columns.name'),
        visible: true,
        sortable: true
      },
      {
        key: 'extension',
        label: t('parkingLots.columns.extension'),
        visible: true,
        sortable: true
      },
      {
        key: 'totalPositions',
        label: t('parkingLots.columns.totalPositions'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: ParkingLot) => (
          <span className="font-mono font-medium text-primary">{item?.totalPositions || 0}</span>
        )
      },
      {
        key: 'occupiedSlots',
        label: t('parkingLots.columns.occupiedSlots'),
        visible: true,
        sortable: false,
        render: (_value: unknown, item: ParkingLot) => {
          const occupied = item?.occupiedSlots ?? 0;
          const total = item?.totalPositions ?? 0;
          return (
            <span className="text-sm">
              {occupied} / {total}
            </span>
          );
        }
      },
      {
        key: 'availableSlots',
        label: t('parkingLots.columns.availableSlots'),
        visible: true,
        sortable: false,
        render: (_value: unknown, item: ParkingLot) => (
          <span className="text-sm text-green-600 dark:text-green-400">
            {item?.availableSlots ?? Math.max(0, (item?.totalPositions ?? 0) - (item?.occupiedSlots ?? 0))}
          </span>
        )
      },
      {
        key: 'enabled',
        label: t('parkingLots.columns.enabled'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: ParkingLot) => (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item?.enabled
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
              }`}
          >
            {item?.enabled ? t('common.enabled') : t('common.disabled')}
          </span>
        )
      },
      {
        key: 'actions',
        label: t('common.actions'),
        visible: true,
        sortable: false
      }
    ];
  }, [t, initialColumns]);

  const dataGrid = useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy,
    initialSortOrder,
    initialLimit,
    moduleName: 'parking-lots'
  });

  // Refresh data when domainId changes
  useEffect(() => {
    if (domainId !== undefined) {
      dataGrid.refresh();
    }
  }, [domainId]);

  return dataGrid;
}

